import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const CartPage = () => {
  const { cart, guestCart, updateQuantity, removeFromCart, loading } = useCart();
  const { isAuthenticated } = useAuth();

  // Use guest cart if not authenticated
  const activeCart = isAuthenticated ? cart : guestCart;

  if (activeCart.items.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center px-6">
        <ShoppingBag className="w-16 h-16 text-white/20 mb-6" />
        <h1 className="font-heading text-3xl text-white mb-4">Coșul tău este gol</h1>
        <p className="font-body text-white/50 mb-8">Se pare că nu ai adăugat nimic încă</p>
        <Link to="/shop">
          <Button className="bg-[#c00000] text-white rounded-none px-8 py-4 font-heading tracking-wider" data-testid="shop-btn">
            ÎNCEPE CUMPĂRĂTURILE
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight uppercase text-white mb-12" data-testid="cart-title">
            COȘUL TĂU
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {activeCart.items.map((item, idx) => (
                <motion.div
                  key={`${item.product_id}-${item.size}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-6 p-6 bg-[#121212] border border-white/5"
                  data-testid={`cart-item-${idx}`}
                >
                  <div className="w-28 h-28 bg-[#1a1a1a] flex-shrink-0">
                    <img
                      src={item.product_image || item.image}
                      alt={item.product_name || item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="font-heading font-bold text-lg text-white">{item.product_name || item.name}</h3>
                      <p className="font-body text-sm text-white/50 mt-1">Mărime: EU {item.size}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.size, item.quantity - 1)}
                          className="w-8 h-8 border border-white/10 flex items-center justify-center text-white hover:border-white/30"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="font-body text-white w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.size, item.quantity + 1)}
                          className="w-8 h-8 border border-white/10 flex items-center justify-center text-white hover:border-white/30"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="font-heading font-bold text-lg text-[#c00000]">
                        {(item.price * item.quantity).toFixed(2)} RON
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.product_id, item.size)}
                    className="text-white/30 hover:text-[#c00000] transition-colors"
                    data-testid={`remove-item-${idx}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </motion.div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#121212] border border-white/5 p-8 sticky top-28">
                <h2 className="font-heading font-bold text-xl tracking-wider text-white mb-6">SUMAR COMANDĂ</h2>
                
                <div className="space-y-4 pb-6 border-b border-white/5">
                  <div className="flex justify-between font-body text-white/60">
                    <span>Subtotal</span>
                    <span>{activeCart.subtotal.toFixed(2)} RON</span>
                  </div>
                  <div className="flex justify-between font-body text-white/60">
                    <span>Livrare</span>
                    <span className="text-[#c00000]">GRATUITĂ</span>
                  </div>
                </div>

                <div className="flex justify-between items-center py-6">
                  <span className="font-heading font-bold text-lg text-white">TOTAL</span>
                  <span className="font-heading font-bold text-2xl text-[#c00000]" data-testid="cart-total">
                    {activeCart.total.toFixed(2)} RON
                  </span>
                </div>

                <Link to="/checkout">
                  <Button 
                    className="w-full bg-[#c00000] text-white hover:bg-[#a00000] rounded-none py-6 font-heading font-bold tracking-wider uppercase"
                    data-testid="checkout-btn"
                  >
                    FINALIZEAZĂ COMANDA
                  </Button>
                </Link>

                <div className="mt-6 pt-6 border-t border-white/5">
                  <div className="flex items-center gap-2 text-white/40 text-sm">
                    <span>🔒</span>
                    <span className="font-body">Checkout Securizat</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CartPage;
